import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import 'rsuite/dist/rsuite.min.css';
import {DateRangePicker } from 'rsuite';
import {
    BreadCrumb,
    Table,
    Pagination,
    Button,
    SpinnerCircular,
    Notification,
} from '@weirgroup/weir-dls'

import { getTableHeaders, groupData } from '../../utils/table.util'
import {
    NULL,
    SELECTED_ASSET,
    UNDEFINED,
    locationHistoryTableHeaders,
} from '../../constants/bucket.contants'
import {
    BucketDataType,
    LocationHistoryParamsType,
    LocationHistoryType,
    SelectedPingType,
    TagType,
} from '../../types/bucketTypes'

import { DisplayMap } from '../../components/map/map'
import assetsApi from '../../api/assets.api'
import { routePath } from '../../constants/navigation.constants'

import './styles.scss'
import { fetchBucketList } from '../../utils/getTagData.utils'
import { useDarkMode } from '../../hooks/use-darkmode'

interface Props {
    selectedAsset: BucketDataType
}

type ToastDataType = {
        variant: string
        message: string
    }
export const LocationHistoryPage = (props: Props) => {
    const { selectedAsset } = props
    const { t } = useTranslation()
    const navigate = useNavigate()

    const menuItems: string[] = [
        t('homepage'),
        selectedAsset?.serial_number,
        t('assetLocationHistory'),
    ]
    const sevenDaysAgo = dayjs(dayjs()).subtract(7, 'days')
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [locationHistory, setLocationHistory] = useState<
        LocationHistoryType[]
    >([])
    const [selectedPing, setSelectedPing] = useState<
        SelectedPingType | undefined
    >(undefined)
    const [showToast, setShowToast] = useState<boolean>(false)
    
    const [toastData, setToastData] = useState<ToastDataType>()
    const [tagData, setTagData] = useState<TagType>()
    const [dateRange, setDateRange] = useState<[Date, Date]>([sevenDaysAgo.toDate(), dayjs().toDate()])
    const {darkMode} = useDarkMode();
    // Reusable function related
    useEffect(() => {
        // “I utilized the fetchBucketList function from the utils module to set the tag data.
        ;(async () => {
            fetchBucketList(selectedAsset)
                .then((data) => {
                    setTagData(data.fetchedAssets)
                })
                .catch((e) => {
                    throw e
                })
                .catch((error: Error) => {})
        })()
    }, [])

    useEffect(() => {
        if (showToast) {
            setTimeout(() => setShowToast(false), 5000)
        }
    }, [showToast])

    useEffect(() => {
        setIsLoading(true)
        let asset
        if (!selectedAsset) {
            let sessionData: string | null = sessionStorage.getItem(SELECTED_ASSET)
            if (sessionData === UNDEFINED || sessionData === NULL || sessionData === null) {
                goToHomepage()
            } else {
                asset = JSON.parse(sessionData)
                const params = { asset_id: asset.asset_id }
                fetchLocationHistory(params)
            }
        } else if (selectedAsset) {
            asset = selectedAsset
            const params = { asset_id: asset.asset_id }
            fetchLocationHistory(params)
        } else {
            goToHomepage()
        }
    }, [])

    const tableHeader = useMemo(
        () => getTableHeaders(locationHistoryTableHeaders),
        []
    )

    const goToHomepage = () => {
        navigate(routePath.HOMEPAGE)
    }

    const pagedData = useMemo(() => {
        if (locationHistory.length > 0) {
            // Sort by created_at descending (latest first)
            const sorted = [...locationHistory].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
            // Format created_at for display
            sorted.forEach((data: LocationHistoryType) => {
                data.created_at = dayjs(data.created_at).format('lll');
            });
            return groupData(sorted, 5);
        } else {
            return [];
        }
    }, [locationHistory]);

    const fetchLocationHistory = (params: LocationHistoryParamsType) => {
        assetsApi
            .getLocationHistory(params)
            .then((result: any) => {
                if (result.data.data.length > 0) {
                    setLocationHistory(result.data.data)
                } else {
                    setLocationHistory([])
                }
                setIsLoading(false)
            })
            .catch(() => {
                setIsLoading(false)
            })
    }

    const showValidationMessage = () => {
        setToastData({
            variant: 'critical',
            message: t('dateRangeValidation'),
        })
        setShowToast(true)
    }

    const applyDateFilter = () => {
        setIsLoading(true)
        const formattedStartDate = dayjs(dateRange[0]).format('MM/DD/YYYY');
        const formattedEndDate = dayjs(dateRange[1]).format('MM/DD/YYYY');
        const params = {
            asset_id: selectedAsset.asset_id,
            start_date: formattedStartDate,
            end_date: formattedEndDate,
        }
        if (new Date(params.start_date) <= new Date(params.end_date)) {
            fetchLocationHistory(params)
        } else {
            showValidationMessage()
            setIsLoading(false)
        }
    }   
    const onDateRangePickerChange = (
        value: [Date | null, Date | null] | null,
        _event: React.SyntheticEvent<Element, Event>
    ) => {
        if (value && Array.isArray(value) && value[0] && value[1]) {
            setDateRange([value[0], value[1]]);
        }
    }

    const tableRowClick = (event: any) => {
        if (selectedPing?.id == event.tag_id.value) {
            setSelectedPing(undefined)
        } else {
            const selectedPingData: SelectedPingType = {
                id: event.tag_id.value,
                lat: event.latitude.value,
                lng: event.longitude.value,
            }
            setSelectedPing(selectedPingData)
        }
    }

    useEffect(()=>{
        applyDateFilter()
    },[])
    return (
        <>
            <div className="location-history" >
                <div className='location-history-grid' style={{backgroundColor:darkMode ? '#3B434C' : '#F6F7F8'}}>
                    <div className="list-grid">
                        <div className="breadcrumbs">
                            <BreadCrumb
                                darkMode={darkMode}
                                menuItems={menuItems}
                                onItemClick={() => goToHomepage()}
                            />
                        </div>
                        <div className="date-picker">
                            <DateRangePicker
                            format='yyyy/MM/dd'
                            character=' - '
                            onClean={() => {
                              setDateRange([sevenDaysAgo.toDate(), dayjs().toDate()]);
                            }}
                            onChange={onDateRangePickerChange}
                            value={dateRange}
                            
                             />
                            <Button
                                    darkMode={darkMode}
                                    label={t('apply')}
                                    size={'sm'}
                                    onClick={() => applyDateFilter()}
                                    full={false}
                                    variant={'primary'}
                            />
                        </div>
                        <div className="table">                           
                            {pagedData.length > 0 ? (
                                 <Table
                                 darkMode={darkMode}
                                 header={tableHeader}
                                 source={pagedData[currentPage - 1]}
                                 title={t('locationHistory')}
                                 tableType={'small'}
                                 rowClickable={true}
                                 onRowClick={(e: any) => tableRowClick(e)}
                             />
                            ) : (
                                <div className="noResults">
                                    <p style={{color:darkMode ? 'white' : '#76808a'}}>{t('noResultsFound')}</p>
                                </div>
                            )}
                            {isLoading ? (
                                <div className="spinner">
                                    <SpinnerCircular darkMode={darkMode} />
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                        <div className="pagination-container">
                            {pagedData.length > 0 ? (
                                <Pagination
                                    currentPage={currentPage}
                                    totalRows={pagedData.length}
                                    pageRows={0}
                                    onPrevious={() =>
                                        setCurrentPage(
                                            currentPage > 1
                                                ? currentPage - 1
                                                : 1
                                        )
                                    }
                                    onNext={() =>
                                        setCurrentPage(
                                            currentPage < pagedData.length
                                                ? currentPage + 1
                                                : pagedData.length
                                        )
                                    }
                                    onSelect={(index: number) =>
                                        setCurrentPage(index)
                                    }
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>
                    <div className='map-grid'>
                        <DisplayMap
                            isLocationHistory={true}
                            locationHistory={locationHistory}
                            selectedPing={selectedPing}
                            tagData={tagData}
                            zoom={selectedPing ? 20 : 5}
                        />
                    </div>
                </div>
            </div>
            {showToast ? (
                <div className="toast-container">
                  <Notification 
                        darkMode={darkMode}
                        title={toastData?.variant === "success" ? "Success" : "Fail"}
                        textContent={toastData?.message}
                        variant={toastData?.variant}
                        onClose={() => setShowToast(false)}
                    />
                </div>
            ) : (
                <></>
            )}
            </>
    )
}
